import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';

import { useTranslation } from 'react-i18next';
import { ReturnStatusStatistic, ReturnStatusType } from 'returnCenter-api';
import { styled } from 'styled-components';
import { getStatusColor } from 'components/Orders/OrderStatus';
import { getStatsTranslations } from 'utils/helpers';

type StatusStatistic = {
    status?: number;
    value?: number;
};

type ReturnCounterCardProps = {
    statistics: StatusStatistic | ReturnStatusStatistic;
    label?: string;
    onClick: () => void;
    isSelected?: boolean;
    isChanging?: boolean;
    noResults?: boolean;
    disabled?: boolean;
    /** Shows a warning triangle with this as its tooltip, e.g. completed returns needing B2C attention. */
    warning?: string;
};

const StatusCard = ({
    statistics,
    label,
    onClick,
    isSelected,
    isChanging,
    noResults,
    disabled,
    warning,
}: ReturnCounterCardProps) => {
    const { t } = useTranslation();

    return (
        <StyledCard
            isChanging={isChanging}
            selected={isSelected}
            status={statistics.status as ReturnStatusType | undefined}
            onClick={onClick}
            noResults={noResults}
            className="status-card"
            disabled={disabled}
        >
            <Container $grow={1} className="card-content" justify="center">
                <Container className="p-2" $grow={1} column>
                    <LabelWrapper justify="between">
                        <Label fontSize={11} pointer={!noResults} as="label">
                            {label ?? getStatsTranslations(t, statistics.status as ReturnStatusType | undefined)}
                        </Label>
                        <StyledIcon className="ml-1" icon={faArrowRight} />
                    </LabelWrapper>
                    <CountWrapper align="center" justify="between">
                        {isChanging ? <Spinner color="text" /> : <Count>{statistics.value}</Count>}
                        {warning && (
                            <Warning title={warning} role="img" aria-label={warning}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Warning>
                        )}
                    </CountWrapper>
                </Container>
            </Container>
        </StyledCard>
    );
};

export default StatusCard;

const StyledCard = styled(Flex)<{
    selected?: boolean;
    isChanging?: boolean;
    noResults?: boolean;
    disabled?: boolean;
    status?: ReturnStatusType;
}>`
    border-radius: var(--radius);
    background-color: ${({ selected, status, theme }) =>
        selected
            ? `${getStatusColor(status ?? null)}20` // the 20 is 20% opacity of the status color when selected
            : `${theme.colors.fillWhite}`};
    border: 1px solid
        ${({ selected, status, theme }) =>
            selected ? `${getStatusColor(status ?? null)}` : `${theme.colors.borderLight}`};
    cursor: ${({ isChanging, noResults }) => (isChanging || noResults ? 'default' : 'pointer')};
    padding: 0.15rem;
    width: 115px;
    height: 90px;

    &:hover {
        background-color: ${({ isChanging, noResults, selected, status, theme }) =>
            isChanging || noResults
                ? 'none'
                : selected
                ? `${getStatusColor(status ?? null)}20`
                : `${theme.colors.fillHover}`};
        transition: transform 0.25s, box-shadow 0.25s, background-color 0.25s;
    }

    ${({ disabled }) =>
        disabled &&
        `
      pointer-events: none;
      opacity: 0.5;
      /* Remove hover/focus styles when disabled */
      &:hover, &:focus {
        background: inherit;
        box-shadow: none;
        cursor: default;
      }
    `}

    @media (max-width: 992px) {
        width: 95px;
        height: 75px;
        padding: 0;
    }
`;

const Container = styled(Flex)`
    align-self: start;

    @media (max-width: 992px) {
        gap: 0.25rem;
    }
`;

const LabelWrapper = styled(Flex)`
    height: 36px;
    width: 100%;

    @media (max-width: 992px) {
        font-size: 14px;
        gap: 1rem;
        height: 28px;
    }
`;

const CountWrapper = styled(Flex)``;

const Warning = styled.span`
    color: ${({ theme }) => theme.colors.warning};
    font-size: 18px;
    line-height: 1;
`;
const Count = styled(Text)`
    font-size: 24px;
    font-weight: 500;
    line-height: 29.05px;
    margin: 0;

    @media (max-width: 992px) {
        font-size: 15px;
    }
`;
const Label = styled(Text)`
    text-align: left;
`;

const StyledIcon = styled(FontAwesomeIcon)``;
